import React from 'react';
import { createPortal } from 'react-dom';
import { useEscapeClose } from '../hooks/useEscapeClose';

/**
 * Drei-Punkte-Menü per Portal: rechtsbündig am Trigger, klappt nach oben, wenn unten
 * kein Platz ist, und wird nicht von `overflow`-Containern (Karten, Listen) abgeschnitten.
 */
export function useAnchoredMenu({ panelWidth, panelHeight }: { panelWidth: number; panelHeight: number }) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = Math.min(Math.max(8, rect.right - panelWidth), window.innerWidth - panelWidth - 8);
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < panelHeight + 24 ? rect.top - 4 - panelHeight : rect.bottom + 4;
      setPos({ top, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, panelWidth, panelHeight]);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEscapeClose(() => setOpen(false), open);

  return { open, setOpen, triggerRef, panelRef, pos };
}

export function AnchoredMenuPanel({
  menu,
  width,
  className,
  children,
}: {
  menu: Pick<ReturnType<typeof useAnchoredMenu>, 'open' | 'panelRef' | 'pos'>;
  width: number;
  className?: string;
  children: React.ReactNode;
}) {
  if (!menu.open || !menu.pos) return null;
  return createPortal(
    <div
      ref={menu.panelRef}
      role="menu"
      style={{ top: menu.pos.top, left: menu.pos.left, width }}
      className={`fixed z-[80] rounded-lg border border-app-border bg-app-surface p-1 shadow-lg ${className ?? ''}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}
